import React, { useEffect, useState } from "react";
import { MoreVertical } from "lucide-react";
import { useAddress } from "../../context/AddressContext";

function MyAddress() {
  const { addresses, fetchAddresses, addAddress, updateAddress, deleteAddress } =
    useAddress();

  const [openMenu, setOpenMenu] = useState(null);
  const [view, setView] = useState("list");
  const [editAddress, setEditAddress] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // 🚀 fetch addresses on load
  useEffect(() => {
    fetchAddresses();
  }, []);

  const handleSave = (newAddr) => {
    addAddress(newAddr);
    setView("list");
  };

  const handleUpdate = (id, updatedAddr) => {
    updateAddress(id, updatedAddr);
    setView("list");
    setEditAddress(null);
  };

  const handleEdit = (addr) => {
    setEditAddress(addr);
    setView("edit");
    setOpenMenu(null);
  };

  const confirmDelete = () => {
    deleteAddress(deleteId);
    setDeleteId(null);
    setOpenMenu(null);
  };

  // ⚡ Set as primary
  const handleSetPrimary = (addr) => {
    updateAddress(addr.id, { ...addr, isPrimary: true });
  };

  if (view === "add" || view === "edit") {
    return (
      <AddEditAddress
        onSave={
          view === "edit"
            ? (addr) => handleUpdate(editAddress.id, addr)
            : handleSave
        }
        onCancel={() => setView("list")}
        title={view === "edit" ? "Edit Address" : "Add Address"}
        existing={view === "edit" ? editAddress : null}
      />
    );
  }

  return (
    <main className=" bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-blue-900">My Address</h1>
        <button
          onClick={() => setView("add")}
          className="flex items-center gap-2 text-blue-900 font-semibold border border-blue-500 px-3 py-1 rounded-lg hover:bg-blue-100 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label="Add new address"
        >
          <span className="text-xl" aria-hidden="true">+</span> Add Address
        </button>
      </div>

      {/* Address List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        {addresses.map((addr) => (
          <article
            key={addr.id}
            className="bg-white p-4 rounded-xl shadow-sm border flex justify-between items-start relative w-full min-h-[140px] transition-all"
          >
            <div className="pr-8">
              <h2 className="font-bold text-blue-900">{addr.name}</h2>
              <p className="text-gray-600 text-sm mt-1">
                {addr.address}, {addr.city}, {addr.country}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {addr.countryCode} {addr.phone}
              </p>
            </div>

            <div className="flex flex-col items-end">
              {/* Primary Selector */}
              <button
                onClick={() => handleSetPrimary(addr)}
                className="mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
                title="Set as primary"
                aria-label={`Set ${addr.name} as primary address`}
              >
                <span
                  className={`w-3 h-3 rounded-full block ${
                    addr.isPrimary ? "bg-green-500" : "bg-gray-300"
                  }`}
                  aria-hidden="true"
                />
              </button>

              {/* More Menu */}
              <div className="relative">
                <button
                  onClick={() =>
                    setOpenMenu(openMenu === addr.id ? null : addr.id)
                  }
                  className="p-1 hover:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label={`More options for ${addr.name}`}
                  aria-expanded={openMenu === addr.id}
                  aria-haspopup="true"
                >
                  <MoreVertical size={18} className="text-gray-600" />
                </button>

                {openMenu === addr.id && (
                  <div className="absolute top-8 right-0 bg-white border shadow-md rounded-lg py-2 w-32 text-sm z-50" role="menu">
                    <button
                      onClick={() => handleEdit(addr)}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                      role="menuitem"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteId(addr.id)}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600 focus:outline-none focus:bg-gray-100"
                      role="menuitem"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-lg z-50" role="dialog" aria-modal="true" aria-labelledby="delete-title">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80">
            <h2 id="delete-title" className="text-lg font-semibold text-gray-800 mb-4">
              Are you sure you want to delete this address?
            </h2>
            <div className="flex gap-4">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

/* ---------- Add/Edit Address Component ---------- */
function AddEditAddress({ onSave, onCancel, title, existing }) {
  const [formData, setFormData] = useState(
    existing || {
      name: "",
      phone: "",
      countryCode: "+91",
      address: "",
      pin: "",
      city: "",
      state: "",
    }
  );
  const [isPrimary, setIsPrimary] = useState(existing?.isPrimary || false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Validation functions
  const validateName = (name) => {
    const alphabetRegex = /^[A-Za-z\s]+$/;
    if (!name.trim()) {
      return "Full name is required";
    }
    if (!alphabetRegex.test(name)) {
      return "Name should contain only alphabets";
    }
    return "";
  };

  const validatePhone = (phone) => {
    const numericRegex = /^\d+$/;
    if (!phone.trim()) {
      return "Contact number is required";
    }
    if (!numericRegex.test(phone)) {
      return "Phone number should contain only digits";
    }
    if (phone.length !== 10) {
      return "Phone number must be exactly 10 digits";
    }
    return "";
  };

  const validateAddress = (address) => {
    if (!address.trim()) {
      return "Street address is required";
    }
    return "";
  };

   const validateCity = (city) => {
    const alphabetRegex = /^[A-Za-z\s]+$/;
    if (!city.trim()) {
      return "City is required";
    }
    if (!alphabetRegex.test(city)) {
      return "City should contain only alphabets";
    }
    return "";
  };

  const validatePin = (pin) => {
    const numericRegex = /^\d+$/;
    if (!pin.trim()) {
      return "Postal code is required";
    }
    if (!numericRegex.test(pin)) {
      return "PIN should contain only digits";
    }
    if (pin.length !== 6) {
      return "PIN code must be 6 digits";
    }
    return "";
  };

  // Handle field blur (when user leaves a field)
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });

    let error = "";
    switch (name) {
      case "name":
        error = validateName(formData[name]);
        break;
      case "phone":
        error = validatePhone(formData[name]);
        break;
      case "address":
        error = validateAddress(formData[name]);
        break;
      case "pin":
        error = validatePin(formData[name]);
        break;
      case "city":
        error = validateCity(formData[name]);
        break;
      default:
        break;
    }

    setErrors({ ...errors, [name]: error });
  };

  // Check form validity (for button enable/disable)
  useEffect(() => {
    const nameError = validateName(formData.name);
    const phoneError = validatePhone(formData.phone);
    const addressError = validateAddress(formData.address);
    const pinError = validatePin(formData.pin);
    const cityError = validateCity(formData.city);

    const hasErrors =
      nameError || phoneError || addressError || pinError || cityError;
    const hasRequiredFields =
      formData.name && formData.phone && formData.address && formData.pin && formData.city;

    setIsFormValid(!hasErrors && hasRequiredFields);
  }, [formData]);

  // Handle input changes with validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;

    // Filter input based on field type
    if (name === "name") {
      // Allow only alphabets and spaces
      filteredValue = value.replace(/[^A-Za-z\s]/g, "");
    } else if (name === "phone") {
      // Allow only numeric input and limit to 10 digits
      filteredValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "pin") {
      // Allow only numeric input for PIN
      filteredValue = value.replace(/\D/g, "");
    }

    setFormData({ ...formData, [name]: filteredValue });

    // Validate the field only if it has been touched
    if (touched[name]) {
      let error = "";
      switch (name) {
        case "name":
          error = validateName(filteredValue);
          break;
        case "phone":
          error = validatePhone(filteredValue);
          break;
        case "address":
          error = validateAddress(filteredValue);
          break;
        case "pin":
          error = validatePin(filteredValue);
          break;
        default:
          break;
      }

      setErrors({ ...errors, [name]: error });
    }
  };

  // Handle field blur (when user leaves a field)
  
  // Check form validity (for button state only, not for showing errors)
  useEffect(() => {
    const nameError = validateName(formData.name);
    const phoneError = validatePhone(formData.phone);
    const addressError = validateAddress(formData.address);
    const pinError = validatePin(formData.pin);

    const hasErrors = nameError || phoneError || addressError || pinError;
    const hasRequiredFields = formData.name && formData.phone && formData.address;

    setIsFormValid(!hasErrors && hasRequiredFields);
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Final validation check
    const nameError = validateName(formData.name);
    const phoneError = validatePhone(formData.phone);
    const addressError = validateAddress(formData.address);
    const pinError = validatePin(formData.pin);

    if (nameError || phoneError || addressError || pinError) {
      setErrors({
        name: nameError,
        phone: phoneError,
        address: addressError,
        pin: pinError,
      });
      return;
    }

    const payload = {
      ...formData,
      isPrimary,
    };
    onSave(payload);
  };

  return (
    <main className=" bg-gray-50 p-6 flex flex-col items-center">
      <div className="bg-white shadow-md rounded-xl p-6 w-full max-w-2xl">
        <h1 className="text-xl font-bold text-blue-900 mb-4">{title}</h1>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Name + Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Full Name"
                className={`border-b outline-none p-2 ${
                  touched.name && errors.name ? "border-red-500" : "border-gray-300"
                }`}
                aria-label="Full Name"
                aria-describedby={touched.name && errors.name ? "name-error" : undefined}
                aria-invalid={!!(touched.name && errors.name)}
                autoFocus
                required
              />
              {touched.name && errors.name && (
                <span
                  id="name-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.name}
                </span>
              )}
            </div>

            <div className="flex flex-col">
              <div
                className={`flex items-center gap-2 border-b p-2 ${
                  touched.phone && errors.phone ? "border-red-500" : "border-gray-300"
                }`}
              >
                {/* <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="outline-none bg-transparent text-gray-700 focus:ring-2 focus:ring-blue-500"
                  aria-label="Country Code"
                >
                  <option value="+1">+1</option>
                </select> */}
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Contact Number"
                  className="flex-1 outline-none"
                  aria-label="Contact Number"
                  aria-describedby={touched.phone && errors.phone ? "phone-error" : undefined}
                  aria-invalid={!!(touched.phone && errors.phone)}
                  maxLength="10"
                  required
                />
              </div>
              {touched.phone && errors.phone && (
                <span
                  id="phone-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.phone}
                </span>
              )}
            </div>
          </div>

          {/* Address + Pin */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Street Address"
                className={`border-b outline-none p-2 ${
                  touched.address && errors.address ? "border-red-500" : "border-gray-300"
                }`}
                aria-label="Street Address"
                aria-describedby={touched.address && errors.address ? "address-error" : undefined}
                aria-invalid={!!(touched.address && errors.address)}
                required
              />
              {touched.address && errors.address && (
                <span
                  id="address-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.address}
                </span>
              )}
            </div>
            <div className="flex flex-col">
              <input
                type="text"
                name="pin"
                value={formData.pin}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Postal Code"
                className={`border-b outline-none p-2 ${
                  touched.pin && errors.pin ? "border-red-500" : "border-gray-300"
                }`}
                aria-label="Postal Code"
                aria-describedby={touched.pin && errors.pin ? "pin-error" : undefined}
                aria-invalid={!!(touched.pin && errors.pin)}
              />
              {touched.pin && errors.pin && (
                <span
                  id="pin-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.pin}
                </span>
              )}
            </div>
          </div>

          {/* City */}
          <div className="flex flex-col">
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="City"
              className={`border-b outline-none p-2 ${
                touched.city && errors.city ? "border-red-500" : "border-gray-300"
              }`}
              aria-label="City"
              aria-describedby={touched.city && errors.city ? "city-error" : undefined}
              aria-invalid={!!(touched.city && errors.city)}
              required
            />
            {touched.city && errors.city && (
              <span
                id="city-error"
                className="text-red-500 text-sm mt-1"
                role="alert"
              >
                {errors.city}
              </span>
            )}
          </div>

          <div className="flex flex-col">
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="State"
            className={`border-b outline-none p-2 ${
              touched.state && errors.state ? "border-red-500" : "border-gray-300"
            }`}
            aria-label="State"
            aria-describedby={touched.state && errors.state ? "state-error" : undefined}
            aria-invalid={!!(touched.state && errors.state)}
            required
          />
          {touched.state && errors.state && (
            <span
              id="state-error"
              className="text-red-500 text-sm mt-1"
              role="alert"
            >
              {errors.state}
            </span>
          )}
        </div>

          {/* Primary Switch */}
          <div className="flex items-center justify-between">
            <label
              htmlFor="primary-toggle"
              className="text-blue-900 font-semibold cursor-pointer"
            >
              Save as primary address
            </label>
            <button
              type="button"
              id="primary-toggle"
              onClick={() => setIsPrimary(!isPrimary)}
              className={`w-10 h-5 flex items-center rounded-full p-1 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isPrimary ? "bg-green-500" : "bg-gray-300"
              }`}
              aria-pressed={isPrimary}
              aria-label={`Set as primary address: ${isPrimary ? "enabled" : "disabled"}`}
            >
              <span
                className={`w-4 h-4 bg-white rounded-full shadow-md transform transition ${
                  isPrimary ? "translate-x-5" : "translate-x-0"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              disabled={!isFormValid}
              className={`flex-1 py-3 rounded-lg font-semibold shadow-md transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isFormValid
                  ? "bg-green-500 hover:bg-green-600 text-white focus:ring-green-500 cursor-pointer"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
              aria-label="Save Address"
            >
              Save Address
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-lg font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

export default MyAddress;
